import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_THUMB_MIN_WIDTH = 28;

const buildDragState = () => ({
  isPointerDown: false,
  isDragging: false,
  startX: 0,
  scrollLeft: 0,
  capturedPointerId: null
});

const buildThumbDragState = () => ({
  isDragging: false,
  startX: 0,
  startScrollLeft: 0,
  capturedPointerId: null
});

const useHorizontalScroll = ({
  enabled = true,
  thumbMinWidth = DEFAULT_THUMB_MIN_WIDTH,
  dragStartThreshold = 0,
  clickCancelThreshold = 4
} = {}) => {
  const scrollRef = useRef(null);
  const scrollbarTrackRef = useRef(null);
  const scrollbarThumbRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef(buildDragState());
  const thumbDragStateRef = useRef(buildThumbDragState());
  const wasDraggingRef = useRef(false);
  const scrollBehaviorRef = useRef(null);

  const updateThumb = useCallback(() => {
    const scrollEl = scrollRef.current;
    const trackEl = scrollbarTrackRef.current;
    const thumbEl = scrollbarThumbRef.current;
    if (!scrollEl || !trackEl || !thumbEl) return;

    const { scrollWidth, clientWidth, scrollLeft } = scrollEl;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    setHasOverflow(maxScroll > 0);

    if (maxScroll === 0) {
      thumbEl.style.width = `${trackEl.clientWidth}px`;
      thumbEl.style.transform = 'translateX(0px)';
      return;
    }

    const trackWidth = trackEl.clientWidth;
    const rawThumbWidth = trackWidth * (clientWidth / scrollWidth);
    const thumbWidth = Math.max(thumbMinWidth, Math.round(rawThumbWidth));
    const maxThumbOffset = Math.max(0, trackWidth - thumbWidth);
    const thumbOffset = maxThumbOffset === 0
      ? 0
      : Math.round((scrollLeft / maxScroll) * maxThumbOffset);

    thumbEl.style.width = `${thumbWidth}px`;
    thumbEl.style.transform = `translateX(${thumbOffset}px)`;
  }, [thumbMinWidth]);

  useEffect(() => {
    if (!enabled) return undefined;
    const scrollEl = scrollRef.current;
    if (!scrollEl) return undefined;

    updateThumb();

    const handleScroll = () => updateThumb();
    scrollEl.addEventListener('scroll', handleScroll, { passive: true });

    const resizeObserver = new ResizeObserver(() => updateThumb());
    resizeObserver.observe(scrollEl);
    if (scrollbarTrackRef.current) {
      resizeObserver.observe(scrollbarTrackRef.current);
    }

    return () => {
      scrollEl.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [enabled, updateThumb]);

  useEffect(() => {
    if (!enabled) return undefined;
    const scrollEl = scrollRef.current;
    if (!scrollEl) return undefined;

    const releasePointerCapture = () => {
      const { capturedPointerId } = dragStateRef.current;
      if (capturedPointerId != null && typeof scrollEl.releasePointerCapture === 'function') {
        try {
          scrollEl.releasePointerCapture(capturedPointerId);
        } catch (error) {
          // ignore pointer capture release errors
        }
      }
      dragStateRef.current.capturedPointerId = null;
    };

    const endDrag = () => {
      if (!dragStateRef.current.isPointerDown) return;
      releasePointerCapture();
      dragStateRef.current = buildDragState();
      setIsDragging(thumbDragStateRef.current.isDragging);
      if (scrollBehaviorRef.current !== null) {
        scrollEl.style.scrollBehavior = scrollBehaviorRef.current;
        scrollBehaviorRef.current = null;
      }
      if (wasDraggingRef.current && typeof window !== 'undefined') {
        window.setTimeout(() => {
          wasDraggingRef.current = false;
        }, 0);
      }
    };

    const handlePointerDown = (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      dragStateRef.current = {
        isPointerDown: true,
        isDragging: false,
        startX: event.clientX,
        scrollLeft: scrollEl.scrollLeft,
        capturedPointerId: null
      };
      wasDraggingRef.current = false;
    };

    const handlePointerMove = (event) => {
      const state = dragStateRef.current;
      if (!state.isPointerDown) return;

      const delta = event.clientX - state.startX;
      const distance = Math.abs(delta);

      if (!state.isDragging && distance > dragStartThreshold) {
        state.isDragging = true;
        setIsDragging(true);
        if (scrollBehaviorRef.current === null) {
          scrollBehaviorRef.current = scrollEl.style.scrollBehavior || '';
        }
        scrollEl.style.scrollBehavior = 'auto';
        if (typeof scrollEl.setPointerCapture === 'function') {
          try {
            scrollEl.setPointerCapture(event.pointerId);
            state.capturedPointerId = event.pointerId;
          } catch (error) {
            state.capturedPointerId = null;
          }
        }
      }

      if (distance >= clickCancelThreshold) {
        wasDraggingRef.current = true;
      }

      if (distance > 0) {
        event.preventDefault();
        scrollEl.scrollLeft = state.scrollLeft - delta;
      }
    };

    const handlePointerUp = () => endDrag();
    const handlePointerCancel = () => endDrag();
    const handlePointerLeave = () => endDrag();
    const handleNativeDragStart = (event) => event.preventDefault();

    scrollEl.addEventListener('pointerdown', handlePointerDown);
    scrollEl.addEventListener('pointermove', handlePointerMove);
    scrollEl.addEventListener('pointerup', handlePointerUp);
    scrollEl.addEventListener('pointerleave', handlePointerLeave);
    scrollEl.addEventListener('pointercancel', handlePointerCancel);
    scrollEl.addEventListener('dragstart', handleNativeDragStart);

    return () => {
      scrollEl.removeEventListener('pointerdown', handlePointerDown);
      scrollEl.removeEventListener('pointermove', handlePointerMove);
      scrollEl.removeEventListener('pointerup', handlePointerUp);
      scrollEl.removeEventListener('pointerleave', handlePointerLeave);
      scrollEl.removeEventListener('pointercancel', handlePointerCancel);
      scrollEl.removeEventListener('dragstart', handleNativeDragStart);
      if (scrollBehaviorRef.current !== null) {
        scrollEl.style.scrollBehavior = scrollBehaviorRef.current;
        scrollBehaviorRef.current = null;
      }
      endDrag();
    };
  }, [enabled]);

  const onClickCapture = useCallback((event) => {
    if (!wasDraggingRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    wasDraggingRef.current = false;
  }, []);

  const onTrackPointerDown = useCallback((event) => {
    if (!enabled || !hasOverflow) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const trackEl = scrollbarTrackRef.current;
    const thumbEl = scrollbarThumbRef.current;
    const scrollEl = scrollRef.current;
    if (!trackEl || !scrollEl) return;
    if (thumbEl && event.target === thumbEl) return;

    const trackRect = trackEl.getBoundingClientRect();
    const clickX = event.clientX - trackRect.left;
    const maxScroll = Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth);
    const trackWidth = trackEl.clientWidth;
    const thumbWidth = thumbEl?.getBoundingClientRect().width || 0;
    const maxThumbOffset = Math.max(0, trackWidth - thumbWidth);
    const nextThumbOffset = Math.max(0, Math.min(maxThumbOffset, clickX - thumbWidth / 2));
    const nextScrollLeft = maxThumbOffset === 0 ? 0 : (nextThumbOffset / maxThumbOffset) * maxScroll;

    event.preventDefault();
    scrollEl.scrollLeft = nextScrollLeft;
  }, [enabled, hasOverflow]);

  const onThumbPointerDown = useCallback((event) => {
    if (!enabled || !hasOverflow) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const scrollEl = scrollRef.current;
    const trackEl = scrollbarTrackRef.current;
    const thumbEl = scrollbarThumbRef.current;
    if (!scrollEl || !trackEl || !thumbEl) return;

    event.preventDefault();
    event.stopPropagation();

    thumbDragStateRef.current = {
      isDragging: true,
      startX: event.clientX,
      startScrollLeft: scrollEl.scrollLeft,
      capturedPointerId: event.pointerId ?? null
    };
    setIsDragging(true);

    if (typeof thumbEl.setPointerCapture === 'function') {
      try {
        thumbEl.setPointerCapture(event.pointerId);
      } catch (error) {
        thumbDragStateRef.current.capturedPointerId = null;
      }
    }

    const handlePointerMove = (moveEvent) => {
      if (!thumbDragStateRef.current.isDragging) return;
      const delta = moveEvent.clientX - thumbDragStateRef.current.startX;
      const maxScroll = Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth);
      const trackWidth = trackEl.clientWidth;
      const thumbWidth = thumbEl.getBoundingClientRect().width;
      const maxThumbOffset = Math.max(0, trackWidth - thumbWidth);
      if (maxThumbOffset === 0) return;
      const scrollDelta = (delta / maxThumbOffset) * maxScroll;
      scrollEl.scrollLeft = thumbDragStateRef.current.startScrollLeft + scrollDelta;
    };

    const endThumbDrag = () => {
      if (!thumbDragStateRef.current.isDragging) return;
      const { capturedPointerId } = thumbDragStateRef.current;
      if (capturedPointerId != null && typeof thumbEl.releasePointerCapture === 'function') {
        try {
          thumbEl.releasePointerCapture(capturedPointerId);
        } catch (error) {
          // ignore release errors
        }
      }
      thumbDragStateRef.current = buildThumbDragState();
      setIsDragging(dragStateRef.current.isDragging);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', endThumbDrag);
      window.removeEventListener('pointercancel', endThumbDrag);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', endThumbDrag);
    window.addEventListener('pointercancel', endThumbDrag);
  }, [enabled, hasOverflow]);

  return {
    scrollRef,
    trackRef: scrollbarTrackRef,
    thumbRef: scrollbarThumbRef,
    hasOverflow,
    isDragging,
    onClickCapture,
    onTrackPointerDown,
    onThumbPointerDown
  };
};

export default useHorizontalScroll;
